import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Check, 
  ChevronRight, 
  Home, 
  PhoneCall, 
  Download,
  ShieldCheck,
  Smartphone,
  Calendar,
  ArrowRight
} from 'lucide-react';

const ThankYou: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { orderId } = location.state || { orderId: "774219" };

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-20 pt-12">
      <div className="max-w-[1000px] mx-auto px-6">
        
        {/* --- MAIN HERO SECTION --- */}
        <div className="bg-white rounded-[40px] p-10 md:p-16 border border-slate-100 shadow-sm text-center mb-8 relative overflow-hidden">
          {/* Decorative Background Element */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-1 bg-gradient-to-r from-transparent via-[#10B981] to-transparent opacity-50" />
          
          <div className="relative inline-flex mb-8">
            <div className="absolute inset-0 bg-emerald-100 rounded-full scale-[1.6] animate-pulse opacity-40" />
            <div className="relative bg-[#10B981] text-white p-6 rounded-full shadow-2xl shadow-emerald-200 inline-flex items-center justify-center">
              <Check size={48} strokeWidth={3} />
            </div>
          </div>

          <h1 className="text-3xl md:text-5xl font-black text-slate-900 mb-4 tracking-tight">Order Confirmed!</h1>
          <p className="text-slate-400 text-sm md:text-base font-bold max-w-[400px] mx-auto mb-10">
            Great news! Your request has been received and our team is already on it.
          </p>

          <div className="inline-flex items-center flex-wrap justify-center gap-4">
            <div className="flex items-center gap-2 bg-slate-50 border border-slate-100 px-5 py-3 rounded-2xl">
              <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">Order ID</span>
              <span className="text-sm font-black text-slate-900 leading-none">#{orderId}</span>
            </div>
            <button 
              onClick={() => window.print()}
              className="flex items-center gap-2 text-slate-400 hover:text-[#363C90] font-black text-[10px] uppercase tracking-widest transition-all bg-white border border-slate-100 px-5 py-3 rounded-2xl shadow-sm"
            >
              <Download size={14} /> Download Receipt
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          
          {/* LEFT: STEP TRACKER */}
          <div className="lg:col-span-7">
            <div className="bg-white rounded-[32px] p-8 border border-slate-100 shadow-sm h-full">
              <h3 className="text-sm font-black text-slate-900 mb-10 uppercase tracking-tight flex items-center gap-2">
                Pickup Timeline <ArrowRight size={14} className="text-slate-300" />
              </h3>
              
              <div className="space-y-12 ml-4">
                {/* Step 1 */}
                <div className="relative flex gap-8 items-start">
                  <div className="absolute left-[-17px] top-6 w-0.5 h-14 bg-indigo-50" />
                  <div className="w-8 h-8 rounded-xl bg-indigo-50 text-[#363C90] flex items-center justify-center shrink-0 z-10 shadow-sm">
                    <Check size={16} strokeWidth={3} />
                  </div>
                  <div>
                    <h4 className="font-black text-slate-900 text-sm">Order Placed Successfully</h4>
                    <p className="text-[11px] font-bold text-slate-400 mt-1 uppercase tracking-tight">Status: Completed</p>
                  </div>
                </div>

                {/* Step 2 */}
                <div className="relative flex gap-8 items-start">
                  <div className="absolute left-[-17px] top-6 w-0.5 h-14 bg-indigo-50" />
                  <div className="w-8 h-8 rounded-xl bg-slate-50 text-slate-300 flex items-center justify-center shrink-0 z-10">
                    <Smartphone size={16} />
                  </div>
                  <div>
                    <h4 className="font-black text-slate-500 text-sm tracking-tight opacity-70">Executive Assignment</h4>
                    <p className="text-[11px] font-bold text-slate-400 mt-1 uppercase tracking-tight">Expect SMS/WhatsApp in 30 mins</p>
                  </div>
                </div>

                {/* Step 3 */}
                <div className="flex gap-8 items-start">
                  <div className="w-8 h-8 rounded-xl bg-slate-50 text-slate-300 flex items-center justify-center shrink-0 z-10">
                    <Calendar size={16} />
                  </div>
                  <div>
                    <h4 className="font-black text-slate-500 text-sm tracking-tight opacity-70">Physical Verification</h4>
                    <p className="text-[11px] font-bold text-slate-400 mt-1 uppercase tracking-tight">Final Payout at your doorstep</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT: SUPPORT & ACTION */}
          <div className="lg:col-span-5 space-y-4">
            <div className="bg-[#363C90] rounded-[32px] p-8 text-white shadow-xl shadow-indigo-900/20">
               <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-white/10 rounded-xl">
                    <PhoneCall size={20} />
                  </div>
                  <h4 className="text-sm font-black uppercase tracking-tight">Need Support?</h4>
               </div>
               <p className="text-xs font-bold text-indigo-100 mb-6 leading-relaxed opacity-80">
                  Our team is available 24/7 to assist you with your pickup or payment queries.
               </p>
               <a href="tel:1800123456" className="block text-center py-4 bg-white text-[#363C90] rounded-2xl font-black text-[11px] tracking-[2px] shadow-lg hover:scale-[1.02] transition-transform uppercase">
                  Call 1800-123-456
               </a>
            </div>

            <div className="bg-emerald-50 border border-emerald-100 rounded-[24px] p-6 flex gap-4">
               <ShieldCheck size={20} className="text-emerald-600 shrink-0" />
               <div>
                  <p className="text-[10px] font-black text-emerald-900 uppercase tracking-widest mb-1">Guaranteed Safety</p>
                  <p className="text-[9px] font-bold text-emerald-700 uppercase leading-normal opacity-80">
                    All our executives are background-verified and follow strict safety protocols.
                  </p>
               </div>
            </div>

            <button 
              onClick={() => navigate('/')}
              className="w-full py-5 bg-white border border-slate-100 text-slate-900 rounded-[24px] font-black text-[11px] flex items-center justify-center gap-3 shadow-sm hover:bg-slate-50 transition-all uppercase tracking-[2px]"
            >
              Back to Dashboard <Home size={16} />
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ThankYou;